import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
import { autoCheckInPacts } from '../utils/pactAutoCheckIn';
import { focusColumns, isMissingColumn, warnFocusColumnsMissing, type SessionFocusCapture } from '../lib/sessionFocusCapture';

/**
 * History of completed routine runs (the routine analogue of habit_sessions).
 * Cloud-backed via the `routine_sessions` table; a row is logged when a run
 * ends. Fails gracefully (logged, no throw) if the migration hasn't been run.
 */
export interface RoutineSessionLog {
  id: string;
  routineId: string;
  durationSeconds: number;
  xpEarned: number;
  stepsCompleted: number;
  startedAt: string;
  completedAt: string | null;
  status: 'completed' | 'cancelled';
  /**
   * The run's REAL-economy XP split by category id (e.g. { occupational: 15 }),
   * already level-scaled at write — this is what useUserStats rolls into levels.
   * Holds ONLY break / non-completing minutes (credited to the routine's main
   * domain); it excludes habit/folder/prayer step time AND their completion points
   * (those credit via their own stores), so it does NOT equal `xpEarned` — that
   * stays the full cosmetic headline tally.
   */
  domainXp: Record<string, number>;
  /**
   * The end-of-run attention capture (migration 0212) — feeds the Mind area's
   * Focus & Attention track and NOTHING else (it mints no XP; `domainXp` above is
   * the run's whole economy). Null = never rated, which is how focus work
   * self-selects. See `utils/focusAttention.ts`.
   */
  attention: number | null; // 1–5
  distractions: number | null; // >= 0
}

interface LogInput {
  routineId: string;
  startedAt: string;
  durationSeconds: number;
  xpEarned: number;
  stepsCompleted: number;
  /** Per-domain REAL-economy XP (break/non-completing minutes only; see RoutineSessionLog). */
  domainXp?: Record<string, number>;
  status?: 'completed' | 'cancelled';
}

interface RoutineSessionLogState {
  sessions: RoutineSessionLog[];
  isLoading: boolean;
  error: string | null;
  /** Who the persisted `sessions` cache belongs to (anti-bleed on account switch). */
  cachedUserId: string | null;
  /** Returns the new row's id so the caller can attach a rating to it. */
  logSession: (input: LogInput) => Promise<string | null>;
  /**
   * Attach the end-of-run attention capture to an already-logged run.
   *
   * ⚠️ Deliberately NOT a parameter of `logSession`. The run must be recorded the
   * instant it ends: `RoutineExecution` reads `runtime.stepIndex` off live state
   * that dies with the timer, and a user who abandons the rating prompt must not
   * lose the run (and its `domainXp`) with it. Log first, rate after.
   */
  rateSession: (sessionId: string, focus: SessionFocusCapture) => Promise<void>;
  fetchSessions: (routineId?: string) => Promise<void>;
  getRoutineSessions: (routineId: string) => RoutineSessionLog[];
}

const mapRow = (row: any): RoutineSessionLog => ({
  id: row.id,
  routineId: row.routine_id,
  durationSeconds: row.duration_seconds,
  xpEarned: row.xp_earned,
  stepsCompleted: row.steps_completed,
  startedAt: row.started_at,
  completedAt: row.completed_at,
  status: row.status,
  domainXp: row.domain_xp ?? {},
  attention: row.attention ?? null,
  distractions: row.distractions ?? null,
});

export const useRoutineSessionLogStore = create<RoutineSessionLogState>()(
  persist<RoutineSessionLogState, [], [], any>(
    (set, get) => ({
  sessions: [],
  isLoading: false,
  error: null,
  cachedUserId: null,

  logSession: async (input) => {
    const at = accountGeneration();
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return null;

      const completedAt = new Date().toISOString();
      const { data, error } = await supabase
        .from('routine_sessions')
        .insert({
          user_id: user.id,
          routine_id: input.routineId,
          duration_seconds: Math.max(0, Math.round(input.durationSeconds)),
          xp_earned: Math.max(0, Math.round(input.xpEarned)),
          steps_completed: Math.max(0, input.stepsCompleted),
          domain_xp: input.domainXp ?? {},
          started_at: input.startedAt,
          completed_at: completedAt,
          status: input.status ?? 'completed',
        })
        .select()
        .single();

      // The run is saved — for the account that has since signed out: not this copy's to show.
      if (at !== accountGeneration()) return null;
      if (error) throw error;
      if (data) set({ sessions: [mapRow(data), ...get().sessions], error: null, cachedUserId: user.id });

      // pacted routine completed? publish the pact check-in (silent no-op otherwise)
      if ((input.status ?? 'completed') === 'completed') {
        void autoCheckInPacts({ type: 'routine', id: input.routineId });
      }
      return data?.id ?? null;
    } catch (error: any) {
      set({ error: error.message });
      return null;
    }
  },

  rateSession: async (sessionId, focus) => {
    try {
      const { error } = await supabase
        .from('routine_sessions')
        .update(focusColumns(focus))
        .eq('id', sessionId);

      // Before 0212 this is 42703. The run is already logged and keeps its XP, so
      // the rating is dropped with a warning rather than surfaced as a failure.
      if (error) {
        if (isMissingColumn(error)) {
          warnFocusColumnsMissing();
          return;
        }
        throw error;
      }

      set({
        sessions: get().sessions.map(s =>
          s.id === sessionId ? { ...s, ...focusColumns(focus) } : s,
        ),
        error: null,
      });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  // Boot calls (no routineId) are deduped so the login init storm fetches the
  // table once; targeted per-routine fetches pass straight through.
  fetchSessions: (routineId) => {
    const run = async () => {
    const at = accountGeneration();
    set({ isLoading: true, error: null });
    try {
      // getSession-based check — a network getUser() here can race token
      // refresh across the ~20 boot initializers (the spontaneous-sign-out
      // class of bug fixed 2026-05-31).
      const { user } = await syncManager.safeAuthCheck();
      if (!user) {
        set({ isLoading: false });
        return;
      }

      // Cache-first boot: the full history is persisted locally, so while this
      // user's cache is fresh the boot fetch is skipped entirely. logSession
      // write-through keeps the cache canonical for this device.
      if (!routineId && get().cachedUserId === user.id && isCloudFresh('routine_sessions', user.id)) {
        set({ isLoading: false });
        return;
      }

      let query = supabase
        .from('routine_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });

      if (routineId) query = query.eq('routine_id', routineId);

      const { data, error } = await query;
      // An answer for an account that has since signed out is not this copy's.
      if (at !== accountGeneration()) return;
      if (error) throw error;
      const fetched = (data || []).map(mapRow);
      set((state) => {
        const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
        // A per-routine fetch only returns that routine's rows — MERGE it over
        // the cached slice so the other routines' history (which feeds
        // useUserStats domain XP) survives. A boot fetch is the authoritative
        // full snapshot; a foreign cache (account switch) is always replaced.
        const sessions = routineId && cacheIsOurs
          ? [...fetched, ...state.sessions.filter((s) => s.routineId !== routineId)]
          : fetched;
        return { sessions, cachedUserId: user.id, isLoading: false };
      });
      if (!routineId) markCloudFresh('routine_sessions', user.id);
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
    };
    return routineId ? run() : dedupeInit('routine_sessions', run);
  },

  getRoutineSessions: (routineId) => get().sessions.filter(s => s.routineId === routineId),
    }),
    {
      name: 'routine-session-log-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      // isLoading/error are session flags; only the history cache + its owner
      // roam across reloads.
      partialize: (state) => ({
        sessions: state.sessions,
        cachedUserId: state.cachedUserId,
      }),
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account saw the last
   one's routine runs — and the XP they roll into its levels — until its own load, and a run logged as
   the account signed out landed in the next account's history. */
scopeToAccount(useRoutineSessionLogStore);
